import { inject, Injectable } from '@angular/core';
import { SelectItem } from '../../../shared/interfaces/Iselect';
import { GlobalService } from '../../../shared/services/global.service';
import {
  SPA_TYPE,
  SupplierPoInfo,
  TaxonomyLevels,
  TypeCode,
  VC_FG_BOMTYPE,
  vcFGComponent,
  vcFGComponentCPQ,
} from '../vas-configurator.constants';

export interface ScopedComponentCorrelation {
  // CPQ component's own saleOrderLineRefNumber -> the VAS component it matches, if any.
  cpqRefToVasComponent: Map<string, vcFGComponent>;
  // VAS component object (by reference) -> the CPQ component it matches, if any. Keyed by
  // reference rather than FinishedGoodLineItemId so callers get a correct match even when a VAS
  // component hasn't been assigned a GUID yet (e.g. a brand-new row mid-edit) — a GUID-string key
  // would collide every such row onto the same '' bucket.
  vasIdToCpqComponent: Map<vcFGComponent, vcFGComponentCPQ>;
}

@Injectable({
  providedIn: 'root',
})
export class VcResolver {
  private globalService = inject(GlobalService);

  getFGlineItemShipTypes() {
    return [
      {
        TypeCodeId: 107,
        DisplayOrder: 1,
        Description: 'STK',
        EnumValue: 1,
        Entity: 'FinishedGoodLineItem.ShipType',
        Bomtypes: [
          'InternalAssembly',
          'CutAssembly',
          'ThirdPartyValueAdd',
          'ThirdPartyAssembly',
          'PriceBundle',
          'ServiceBundle',
          'RepeatableValueAdd',
        ],
      },
      {
        TypeCodeId: 106,
        DisplayOrder: 2,
        Description: 'IO',
        EnumValue: 2,
        Entity: 'FinishedGoodLineItem.ShipType',
        Bomtypes: [
          'InternalAssembly',
          'CutAssembly',
          'ThirdPartyValueAdd',
          'ThirdPartyAssembly',
          'PriceBundle',
          'RepeatableValueAdd',
        ],
      },
      {
        TypeCodeId: 105,
        DisplayOrder: 3,
        Description: 'DS',
        EnumValue: 3,
        Entity: 'FinishedGoodLineItem.ShipType',
        Bomtypes: [
          'ThirdPartyValueAdd',
          'ThirdPartyAssembly',
          'ServiceBundle',
        ],
      },
    ];
  }

  resolveShipTypeToEnumValue(shipType: any): number | null {
    if (shipType == null) return null;
    const knownTypes = this.getFGlineItemShipTypes();
    const asNum = Number(shipType);
    if (!isNaN(asNum)) {
      const byId = knownTypes.find((s) => s.EnumValue === asNum);
      if (byId) return byId.EnumValue;
    }
    const byDesc = knownTypes.find((s) => s.Description === String(shipType));
    return byDesc?.EnumValue ?? null;
  }

  resolveShipTypeDescription(enumValue: number | null | undefined): string | null {
    if (enumValue == null) return null;
    return (
      this.getFGlineItemShipTypes().find((t) => t.EnumValue === enumValue)?.Description ?? null
    );
  }

  // VAS-478: entered cost should only be submitted when it reflects a manual override — i.e. it
  // differs from replacement cost. When they match, null it out so the host knows the cost was
  // not manually entered.
  resolveEnteredCostOverride(pricingInfo: Record<string, any> | null | undefined): any {
    if (!pricingInfo) return pricingInfo?.['enteredCost'] ?? null;
    const entered = this.globalService.getOnlyValue(pricingInfo['enteredCost']);
    const replacement = this.globalService.getOnlyValue(pricingInfo['replacementCost']);
    return entered === replacement ? null : pricingInfo['enteredCost'];
  }

  private static readonly PART_TYPE_ENUM: Record<string, number> = {
    ASST: 1,
    ATO: 2,
    OSP: 3,
    PTO: 4,
    REEL: 5,
    STD: 6,
    SVC: 7,
    LBR: 9,
    TPSVC: 10,
  };

  resolvePartTypeToEnumValue(partType: any, fgPartTypes: any[]): number | null {
    if (partType == null) return null;
    const asNum = Number(partType);
    if (!isNaN(asNum) && asNum !== 0) return asNum;
    const str = String(partType);
    const byDesc = fgPartTypes.find((s: any) => s.Description === str);
    if (byDesc) return byDesc.EnumValue;
    return VcResolver.PART_TYPE_ENUM[str] ?? null;
  }

  resolveqUomTypeToEnumValue(quomType: any): number | null {
    if (quomType == null) return null;
    const knownTypes = this.globalService.getDataFromStorage('uom_list');
    const asNum = Number(quomType);
    if (!isNaN(asNum)) {
      const byId = knownTypes.find((s: any) => s.EnumValue === asNum);
      if (byId) return byId.EnumValue;
    }
    const byDesc = knownTypes.find((s: any) => s.Description === String(quomType));
    return byDesc?.EnumValue ?? null;
  }

  resolveUOMDescription(value: number | string | null | undefined): string | null {
    if (value == null) return null;
    const uomList: any[] = this.globalService.getDataFromStorage('uom_list') || [];
    return uomList.find((t: any) => t.EnumValue === value)?.Description ?? null;
  }

  resolveInventoryTypeDescription(enumValue: number | null | undefined): string | null {
    if (enumValue == null) return null;
    const inventoryList =
      this.globalService.getDataFromStorage('lineitem_InventoryType_list') ?? [];
    return inventoryList.find((t: any) => t.EnumValue === enumValue)?.Description ?? null;
  }

  resolveProjectProgramNumbers(projectId: string | null | undefined): {
    ProjectNumber: string | null;
    ProgramNumber: string | null;
  } {
    const value = projectId ?? null;
    const isProject = typeof value === 'string' && value.startsWith('PRJ');
    const isProgram = typeof value === 'string' && value.startsWith('PRG');
    return {
      ProjectNumber: isProject ? value : null,
      ProgramNumber: isProgram || !isProject ? value : null,
    };
  }

  resolveBomTypeKey(detail: any): keyof typeof VC_FG_BOMTYPE | undefined {
    let bomType = detail?.BOMType as keyof typeof VC_FG_BOMTYPE;
    if (bomType === 'ThirdPartyAssembly') {
      bomType = 'ThirdPartyValueAdd' as keyof typeof VC_FG_BOMTYPE;
    }
    if (detail?.CutAssembly) {
      bomType = 'CutAssembly' as keyof typeof VC_FG_BOMTYPE;
    } else if (bomType === 'InternalAssembly' && !detail?.CutAssembly) {
      bomType = 'InternalAssembly' as keyof typeof VC_FG_BOMTYPE;
    }
    return bomType;
  }

  resolveWorkOrderReleaseText(
    workOrderReleaseEnumValue: number | string | null | undefined,
    typeCodeList: TypeCode[] | null | undefined,
  ): string | null {
    if (workOrderReleaseEnumValue == null) return null;
    const match = (typeCodeList ?? [])
      .filter((item) => item.Entity === 'FinishedGood.WorkOrderRelease')
      .find((w) => w.EnumValue === workOrderReleaseEnumValue);
    return match?.Description ?? null;
  }

  resolveShipToAccNum(
    shipToId: string | null | undefined,
    attributeList: any[] | null | undefined,
  ): string | null {
    if (!shipToId) return null;
    return (attributeList ?? []).find((a: any) => a.CId === shipToId)?.AccNum ?? null;
  }

  // Mirrors VcSupplierPoInfo's showFreightAccountNumber()/showFreightAddress() required-field
  // rule: Freight Account # is mandatory for Collect and Third Party; Freight Address is
  // mandatory for Third Party only. Used to gate the main Submit button against a saved-but-
  // incomplete More Info snapshot, since the dialog itself allows closing without filling in
  // required fields.
  isSupplierPoInfoInvalid(supplierPOInfo?: SupplierPoInfo | null): boolean {
    const freightTerms = supplierPOInfo?.FreightTerms ?? null;
    if (freightTerms !== 'Collect' && freightTerms !== 'Third Party') {
      return false;
    }
    if (!supplierPOInfo?.FreightAccountNumber) {
      return true;
    }
    return freightTerms === 'Third Party' && !supplierPOInfo?.FreightAddress;
  }

  // Mirrors the "More Info" gating in VCRow/VcNestedRowHeader (isOspComponent && !isStkComponent)
  // so Submit-time validation flags exactly the components that actually show the More Info option.
  hasMoreInfoCapability(item: any): boolean {
    const partType = item?.PartType;
    const isOsp = partType === 'OSP' || partType === 3;
    return isOsp && item?.ShipType !== 1;
  }

  isNestedFgComponent(item: any): boolean {
    return !!(
      item?.PartType === 'ATO' ||
      item?.PartType === 2 ||
      item?.PartType === 'PTO' ||
      item?.PartType === 4
    );
  }

  // LBR/SVC/3PSVC ShipType is defaulted and OSP's is updated to DS manually in real time —
  // per VAS-46, Org Attributes is never called for these, only for plain stock components.
  hasDefaultedShipType(item: any): boolean {
    return (
      item?.PartType === 'LBR' ||
      item?.PartType === 9 ||
      item?.PartType === 'SVC' ||
      item?.PartType === 7 ||
      item?.PartType === 'OSP' ||
      item?.PartType === 3 ||
      item?.PartType === '3PSVC' ||
      item?.PartType === 10
    );
  }

  // VAS-206: LBR/SVC/3PSVC are excluded from Org Attributes (see hasDefaultedShipType) but
  // multiplePartSearch has no BOM-role awareness and returns the generic non-stock
  // ShipType ("IO") for them too. Per VAS-46 / QPM-2996: LBR & SVC are always STK, 3PSVC
  // is always DS, regardless of BOMType.
  resolveDefaultedShipType(item: any): number | null {
    if (item?.PartType === 'LBR' || item?.PartType === 9) return 1; // STK
    if (item?.PartType === 'SVC' || item?.PartType === 7) return 1; // STK
    if (item?.PartType === '3PSVC' || item?.PartType === 10) return 3; // DS
    if (this.isNestedFgComponent(item)) {
      // VAS-275: an Internal Assembly nested FG always ships STK — the same generic
      // non-stock ShipType PartSearch/MDM returns for LBR/SVC (VAS-206) shows up here
      // too, so the nested FG's own BOMType wins over whatever ShipType it carries.
      if (item?.FGHeaderDetails?.BOMType === 'InternalAssembly') return 1; // STK
      return this.resolveShipTypeToEnumValue(item?.ShipType);
    }
    return null;
  }

  getNumberFromLocationName(location: SelectItem | string | null | undefined): string | null {
    if (!location) return null;
    if (typeof location === 'string') {
      return location.includes(':') ? location.split(':')[0].trim() : location;
    }
    if (!location?.text?.includes(':')) return null;
    return location.text?.split(':')?.[0].trim() ?? null;
  }

  parseJsonField(val: any): any {
    if (!val) return null;
    return typeof val === 'string' ? JSON.parse(val) : val;
  }

  // VAS-436: true when QuotedCost is empty, or still just mirrors the component's
  // ReplacementCost (i.e. auto-populated, never typed in by the user) — in which case SPA
  // Type selection stays allowed. A QuotedCost that diverges from ReplacementCost is a genuine
  // manual override and blocks SPA Type selection. Must be called with the component's state
  // from BEFORE a pending pricing response is applied, same as the original call sites.
  isQuotedCostAutoPopulated(component: any): boolean {
    if (!component?.QuotedCost) {
      return true;
    }
    const pricingInfo = this.parseJsonField(component?.pricingInfo);
    const replacement = this.globalService.getOnlyValue(pricingInfo?.['replacementCost']);
    const quoted = this.globalService.getOnlyValue(component.QuotedCost);
    return quoted === replacement;
  }

  // Mirrors the match predicate used by VcPayload.resolveSupplierNumber — true when siteCode
  // resolves to a real VAS DB Supplier Site, by either its id or its display number.
  isSupplierSiteInLookupList(siteCode: string | null | undefined, siteList: any[] | undefined): boolean {
    if (!siteCode) return false;
    return !!siteList?.some((s: any) => s.SupplierSiteId === siteCode || s.SupplierNumber === siteCode);
  }

  // VAS-355: internal SPA_TYPE enum values (OneTime/Expected/Discretionary) are used throughout
  // the UI and app logic, but the Pricing API and CPQ expect different display-style strings.
  // Single source of truth for that translation — both directions go through this map so VAS's
  // own dropdown/logic never has to know about the external wire values.
  private readonly spaTypeWireMap: Record<string, string> = {
    [SPA_TYPE.OneTime]: 'One Time SPA',
    [SPA_TYPE.Expected]: 'Expected SPA',
    [SPA_TYPE.Discretionary]: 'Selected SPA',
  };

  toSpaTypeWireValue(type: string | null | undefined): string | null {
    return type ? (this.spaTypeWireMap[type] ?? null) : null;
  }

  fromSpaTypeWireValue(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    const entry = Object.entries(this.spaTypeWireMap).find(([, wireValue]) => wireValue === value);
    return entry ? entry[0] : null;
  }

  // VAS-122/VAS-433: flat PascalCase fields spread into the Pricing API recalculation request
  // (getPricingDetails() / getIndividualPricing()) so Flintfox can reprice around the
  // component's current SPA selection. Field names + casing confirmed via a live GetPricing
  // request trace. RequestedSpaType is sent as the CPQ/Pricing wire value, not the internal
  // SPA_TYPE enum value — see spaTypeWireMap.
  buildRequestedSpaFields(component: any): Record<string, unknown> {
    const spa = component?.SpaConfiguration;
    return {
      RequestedSpaType: this.toSpaTypeWireValue(spa?.SPAType),
      RequestedSpaCost: this.globalService.getOnlyValue(spa?.SPACost),
      RequestedSpaNumber: spa?.SPAQuoteNumber ?? null,
      RequestedSpaEffectiveDate: spa?.SPAEffectiveDate ?? null,
      RequestedSpaExpirationDate: spa?.SPAExpirationDate ?? null,
      RequestedSpaSource: spa?.SPASource ?? null,
      RequestedSpaSourceId: spa?.SPASourceId ?? null,
    };
  }

  // VAS-433/INT-1086: builds a single-component GetSPAList request entry — one call per
  // component, mirroring getIndividualPricing()'s single-row array-wrap pattern (caller wraps
  // the returned object in a one-element array). SupplierNumber/ShipType resolution mirrors
  // getIndividualPricing()'s own field sourcing exactly, so both requests stay in sync.
  // ManufacturerCode has no dedicated field on vcFGComponent today — falls back to the same
  // ManufacturerName/ManufacturerId pair GetPricing's own ManufacturerName field already uses,
  // pending backend confirmation (same risk category as the other traced-but-unconfirmed fields).
  buildSpaListRequest(
    component: any,
    fgDetails: any,
    supplierSiteList: any[] | null | undefined,
    isAddFgMode: boolean,
  ): Record<string, unknown> {
    const matchedSite = isAddFgMode
      ? supplierSiteList?.find((s: any) => s.SupplierSiteId === component?.SupplierSiteId)
      : null;
    return {
      BillToCustomer: fgDetails?.customerAccountNumber ?? null,
      DocumentNumber: fgDetails?.salesOrderNumber ?? null,
      SalesLocation: fgDetails?.salesLocationId ?? null,
      LineReference: component?.FinishedGoodLineItemId ?? null,
      PartNumber: component?.PartNumber ?? null,
      ManufacturerCode: component?.ManufacturerName ?? component?.ManufacturerId ?? null,
      MfgPartNumber: component?.MFRPartNumber ?? null,
      UOM: component?.UOM ?? null,
      Quantity: component?.orderQuantity ?? null,
      ShipType: this.resolveShipTypeDescription(component?.ShipType) ?? component?.ShipType ?? null,
      SupplierNumber:
        (isAddFgMode
          ? matchedSite?.SupplierNumber || component?.SupplierSiteId
          : component?.SupplierSiteId) || null,
      ShippingLocation: component?.ShipLocation ?? null,
    };
  }

  // VAS-330: resolves the taxonomy breakdown to embed in a synthetic (fake) PartJson for a
  // T-Part component. If the component already carries a full L1-4 breakdown, copy it as-is.
  // Otherwise, if only a bare taxonomy code is present, return it as a fetch code so the
  // caller can batch it into a single FetchLookupData call alongside any other components
  // that also need resolving, and patch the result in once that call resolves.
  resolveComponentTaxonomy(component: any): {
    fields: TaxonomyLevels | null;
    fetchCode: string | null;
  } {
    const hasBreakdown =
      component?.TaxonomyL1Name != null ||
      component?.TaxonomyL2Name != null ||
      component?.TaxonomyL3Name != null ||
      component?.TaxonomyL4Name != null;

    if (!hasBreakdown) {
      return { fields: null, fetchCode: component?.TaxonomyId ?? null };
    }

    return {
      fields: {
        taxonomyCode1: component?.TaxonomyL1 ?? null,
        taxonomyName1: component?.TaxonomyL1Name ?? null,
        taxonomyCode2: component?.TaxonomyL2 ?? null,
        taxonomyName2: component?.TaxonomyL2Name ?? null,
        taxonomyCode3: component?.TaxonomyL3 ?? null,
        taxonomyName3: component?.TaxonomyL3Name ?? null,
        // TaxonomyId is only a dedicated L4 code when TaxonomyL4Name confirms an L4 level
        // actually exists — otherwise it's just a duplicate of the deepest present L1-3 code.
        taxonomyCode4: component?.TaxonomyL4Name != null ? (component?.TaxonomyId ?? null) : null,
        taxonomyName4: component?.TaxonomyL4Name ?? null,
      },
      fetchCode: null,
    };
  }

  // VAS-330: resolves the taxonomy breakdown for a new T-FG header from the real Part Search
  // PartJson captured for the original FG before its promotion to a T-Part — the only place
  // the original FG's taxonomy data lives, since vcFGHeader has no taxonomy fields of its own.
  resolveHeaderTaxonomyFromPartJson(partJson: any): TaxonomyLevels | null {
    const parsed = this.parseJsonField(partJson);
    const hasBreakdown =
      parsed?.taxonomyCode1 != null ||
      parsed?.taxonomyCode2 != null ||
      parsed?.taxonomyCode3 != null ||
      parsed?.taxonomyCode4 != null;
    if (!hasBreakdown) return null;

    return {
      taxonomyCode1: parsed?.taxonomyCode1 ?? null,
      taxonomyName1: parsed?.taxonomyName1 ?? null,
      taxonomyCode2: parsed?.taxonomyCode2 ?? null,
      taxonomyName2: parsed?.taxonomyName2 ?? null,
      taxonomyCode3: parsed?.taxonomyCode3 ?? null,
      taxonomyName3: parsed?.taxonomyName3 ?? null,
      taxonomyCode4: parsed?.taxonomyCode4 ?? null,
      taxonomyName4: parsed?.taxonomyName4 ?? null,
    };
  }

  // VAS-330: a single FetchLookupData call can be batched with several requested codes at
  // once (see the queued callers in vc-container.ts) — res.Taxonomies comes back as one flat
  // list covering every requested code's ancestor chain. Indexing by TaxonomyId and walking
  // up via ParentTaxonomyId from this one specific `code` (mirrors getLineItemTaxonomyHierarchy
  // in fg-taxonamy.ts) keeps each code's own chain correct even when the response mixes rows
  // from several different requested codes together.
  mapTaxonomyLookupResponse(res: any, code: string): TaxonomyLevels {
    const result: TaxonomyLevels = {
      taxonomyCode1: null,
      taxonomyName1: null,
      taxonomyCode2: null,
      taxonomyName2: null,
      taxonomyCode3: null,
      taxonomyName3: null,
      taxonomyCode4: null,
      taxonomyName4: null,
    };
    const taxonomies: any[] = res?.Taxonomies ?? [];
    const byId = new Map<string, any>(taxonomies.map((t) => [t.TaxonomyId, t]));

    let current = byId.get(code);
    while (current) {
      if (current.Level === 1) {
        result.taxonomyCode1 = current.TaxonomyId ?? null;
        result.taxonomyName1 = current.Name ?? null;
      } else if (current.Level === 2) {
        result.taxonomyCode2 = current.TaxonomyId ?? null;
        result.taxonomyName2 = current.Name ?? null;
      } else if (current.Level === 3) {
        result.taxonomyCode3 = current.TaxonomyId ?? null;
        result.taxonomyName3 = current.Name ?? null;
      } else if (current.Level === 4) {
        result.taxonomyCode4 = current.TaxonomyId ?? null;
        result.taxonomyName4 = current.Name ?? null;
      }
      current = current.ParentTaxonomyId ? byId.get(current.ParentTaxonomyId) : null;
    }
    return result;
  }

  normalizeGP(value: any): number | null {
    if (value == null) return null;
    const num = parseFloat(value.toString().replace(/%/g, '').trim());
    return isNaN(num) ? null : num;
  }

  toNumberOrNull(value: any): number | null {
    if (value == null || value === '') return null;
    const num = Number(value);
    return isNaN(num) ? null : num;
  }

  editValuesEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null && b == null) return true;
    return false;
  }

  // Field-level equality for the built SpaConfiguration CPQ-shape object — buildSpaConfigurationPayload()
  // returns a fresh object on every call, so reference equality (editValuesEqual) can never detect a
  // genuine no-op change. Compares every sub-field and reports equal only when none differ, so
  // unchanged SPA data doesn't spuriously appear in EDIT_FG changeTracking (VAS-122/VAS-433).
  spaConfigurationEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return a == null && b == null;
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
      if (a[key] !== b[key]) return false;
    }
    return true;
  }

  resolveSegmentType(SegmentType: string, Segment: string): string {
    if (SegmentType?.startsWith('CUSTOMER')) {
      return 'COP';
    }
    if (SegmentType?.startsWith('INVENTORY')) {
      return Segment?.startsWith('PRJ') ? 'Project' : 'Program';
    }
    if (SegmentType === 'INVANDCOST') {
      return 'IO';
    }
    if (SegmentType?.startsWith('BILLHOLD')) {
      return 'B & H';
    }
    return SegmentType;
  }
  // Correlates a CPQ kitComponents list against VAS's persisted/working component list.
  //
  // PartNumber is the only identifier CPQ and VAS share (GUIDs are minted independently by
  // each system and can't be trusted across the boundary), but the same PartNumber can appear
  // more than once in one BOM — once at the root assembly level and again inside a nested
  // sub-assembly, or (RepeatableValueAdd BOMs only) more than once at the *same* level, once per
  // RVA stage. A flat PartNumber-keyed Map/Set collapses those into one entry (last write wins),
  // which silently drops one of the two components.
  //
  // This runs two passes. Pass 1 walks both sides' trees in parallel, top-down: at each level it
  // only matches same-level siblings sharing a PartNumber, within the same parent scope (CPQ's
  // parentSaleOrderLineRefNumber lineage vs VAS's ParentFinishedGoodLineItemId lineage). Same-
  // PartNumber duplicates within a level are disambiguated by RVA stage first — CPQ's
  // fgTaskDetail.stageGroupId vs VAS's StageGroupNumber — preferring whichever remaining candidate's
  // stage agrees exactly, and only falling back to positional FIFO pairing when no candidate's
  // stage agrees (see pickCpqMatch below for why stage is preferred, not required). Non-RVA
  // components carry no stageGroupId on either side, so every match there falls back to FIFO —
  // identical to pairing before RVA existed. A child is only walked once its parent has matched,
  // so if the two systems' hierarchies genuinely diverge (VAS's last-saved tree vs a fresh CPQ
  // payload — different ordering, a restructured BOM, etc.) an entire matched-or-not subtree can
  // be skipped by pass 1. Pass 2 is an unscoped, flat fallback over whatever pass 1 left unmatched,
  // so a plain single-occurrence PartNumber still correlates correctly even when the trees don't
  // line up node-for-node — only genuine same-level duplicates depend on pass 1's scoping to
  // disambiguate correctly.
  correlateCpqAndVasComponents(
    cpqComponents: vcFGComponentCPQ[] | null | undefined,
    vasComponents: vcFGComponent[] | null | undefined,
  ): ScopedComponentCorrelation {
    const cpqRefToVasComponent = new Map<string, vcFGComponent>();
    const vasIdToCpqComponent = new Map<vcFGComponent, vcFGComponentCPQ>();

    const cpqList = cpqComponents ?? [];
    const vasList = vasComponents ?? [];

    const matchedCpq = new Set<vcFGComponentCPQ>();
    const matchedVas = new Set<vcFGComponent>();

    // '' whenever stageGroupId is absent, so stage comparisons below degrade to "no stage" for
    // every non-RVA component (and for RVA components on whichever side hasn't been assigned a
    // stage yet — see the note on stage-aware tiebreaking further down).
    const stageSuffix = (stageGroupId: unknown): string =>
      stageGroupId == null ? '' : String(stageGroupId);

    const recordMatch = (cpqMatch: vcFGComponentCPQ, vasNode: vcFGComponent) => {
      if (cpqMatch.saleOrderLineRefNumber) {
        cpqRefToVasComponent.set(cpqMatch.saleOrderLineRefNumber, vasNode);
      }
      vasIdToCpqComponent.set(vasNode, cpqMatch);
      matchedCpq.add(cpqMatch);
      matchedVas.add(vasNode);
    };

    // A root-level (Level 1) component's parentSaleOrderLineRefNumber is NOT reliably omitted —
    // in real EDIT_FG payloads CPQ points it at the FG's own saleOrderLineRefNumber instead (a
    // value that lives outside kitComponents entirely). So "is this a root component" can't be a
    // simple null/undefined check: it has to test whether the parent ref resolves to another
    // component actually present in this same list (mirrors vasByParent's vasIdSet check below).
    // Anything whose parent ref doesn't resolve within the list — whether omitted or pointing at
    // the FG itself — is root.
    const CPQ_ROOT = '';
    const cpqRefSet = new Set(cpqList.map((c) => c.saleOrderLineRefNumber).filter(Boolean));
    const cpqByParent = new Map<string, vcFGComponentCPQ[]>();
    for (const c of cpqList) {
      const pref = c.parentSaleOrderLineRefNumber;
      const key = pref && cpqRefSet.has(pref) ? pref : CPQ_ROOT;
      if (!cpqByParent.has(key)) cpqByParent.set(key, []);
      cpqByParent.get(key)!.push(c);
    }

    const VAS_ROOT = '__vas_root__';
    const vasIdSet = new Set(vasList.map((v) => v.FinishedGoodLineItemId).filter(Boolean));
    const vasByParent = new Map<string, vcFGComponent[]>();
    for (const v of vasList) {
      const pid = v.ParentFinishedGoodLineItemId;
      const key = pid && vasIdSet.has(pid) ? pid : VAS_ROOT;
      if (!vasByParent.has(key)) vasByParent.set(key, []);
      vasByParent.get(key)!.push(v);
    }

    // Picks this VAS node's CPQ match out of its PartNumber's remaining candidate queue. PartNumber
    // is still the primary key — a component can be identified across systems by nothing else — so
    // this only prefers a candidate whose stage agrees exactly with vasNode's; it never requires
    // stage agreement. Requiring it would break plain single-occurrence PartNumbers whenever the
    // two sides' stage data hasn't converged (e.g. VAS's UI assigns StageGroupNumber locally and
    // CPQ's echoed-back kitComponent hasn't caught up yet), wrongly leaving a genuine 1:1 match
    // unpaired. Stage only needs to disambiguate when a PartNumber bucket has more than one
    // candidate — i.e. real same-level duplicates — which is exactly when an exact-stage candidate
    // exists to prefer. When no candidate's stage agrees (non-RVA parts, or asymmetric stage data),
    // falling back to FIFO position reproduces the pre-RVA pairing exactly.
    const pickCpqMatch = (
      queue: vcFGComponentCPQ[] | undefined,
      vasNode: vcFGComponent,
    ): vcFGComponentCPQ | undefined => {
      if (!queue?.length) return undefined;
      const vasStage = stageSuffix(vasNode.StageGroupNumber);
      const stageIdx = queue.findIndex((c) => stageSuffix(c.fgTaskDetail?.stageGroupId) === vasStage);
      return stageIdx >= 0 ? queue.splice(stageIdx, 1)[0] : queue.shift();
    };

    const walk = (cpqParentKey: string, vasParentKey: string) => {
      const cpqSiblings = cpqByParent.get(cpqParentKey) ?? [];
      const vasSiblings = vasByParent.get(vasParentKey) ?? [];

      const cpqQueueByPart = new Map<string, vcFGComponentCPQ[]>();
      for (const c of cpqSiblings) {
        const pn = c.transactablePartNumber ?? '';
        if (!cpqQueueByPart.has(pn)) cpqQueueByPart.set(pn, []);
        cpqQueueByPart.get(pn)!.push(c);
      }

      for (const vasNode of vasSiblings) {
        const pn = vasNode.PartNumber ?? '';
        const cpqMatch = pickCpqMatch(cpqQueueByPart.get(pn), vasNode);
        if (!cpqMatch) continue;

        recordMatch(cpqMatch, vasNode);
        walk(cpqMatch.saleOrderLineRefNumber ?? CPQ_ROOT, vasNode.FinishedGoodLineItemId ?? '');
      }
    };

    walk(CPQ_ROOT, VAS_ROOT);

    // Pass 2 — flat PartNumber fallback, unscoped, for anything pass 1 left unmatched.
    const unmatchedCpqByPart = new Map<string, vcFGComponentCPQ[]>();
    for (const c of cpqList) {
      if (matchedCpq.has(c)) continue;
      const pn = c.transactablePartNumber ?? '';
      if (!unmatchedCpqByPart.has(pn)) unmatchedCpqByPart.set(pn, []);
      unmatchedCpqByPart.get(pn)!.push(c);
    }
    for (const vasNode of vasList) {
      if (matchedVas.has(vasNode)) continue;
      const pn = vasNode.PartNumber ?? '';
      const cpqMatch = pickCpqMatch(unmatchedCpqByPart.get(pn), vasNode);
      if (cpqMatch) recordMatch(cpqMatch, vasNode);
    }

    return { cpqRefToVasComponent, vasIdToCpqComponent };
  }
}
